import { colors, layout, typography } from '../../../styles/theme';
import {
  FILTER_ACCENT,
  FILTER_ACCENT_CONTRAST,
  FILTER_ACCENT_14,
  FILTER_ACCENT_67,
} from '../FilterPanel/FilterPanel.styles';

const HEADER_HEIGHT = layout.mapHeaderHeight;

/** Ancho fijo de la barra de búsqueda (Figma). */
export const SEARCH_BAR_WIDTH = 640;
/** Ancho del bloque búsqueda + gap + botón filtros. */
export const SEARCH_FILTERS_ROW_WIDTH = SEARCH_BAR_WIDTH + 10 + 110;
/** Columna central del header desktop: mismo ancho que el mapa país. */
export const HEADER_WITH_SEARCH_GRID_COLUMNS = `minmax(max-content, 1fr) minmax(0, ${SEARCH_FILTERS_ROW_WIDTH}px) minmax(max-content, 1fr)`;
export const SEARCH_BAR_HEIGHT = 40;
export const FILTER_BUTTON_HEIGHT = 40;
/** Padding vertical de la fila mobile (arriba + abajo). */
export const MOBILE_SEARCH_BAR_PADDING_Y = 10;
/** Alto total de la fila búsqueda/filtros bajo el header (padding + input + borde). */
export const MOBILE_SEARCH_BAR_BLOCK_HEIGHT =
  MOBILE_SEARCH_BAR_PADDING_Y * 2 + SEARCH_BAR_HEIGHT + 1;

export const searchFiltersRowStyle = ({ isMobile = false } = {}) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  width: '100%',
  maxWidth: isMobile ? undefined : `${SEARCH_FILTERS_ROW_WIDTH}px`,
  minWidth: 0,
  flexShrink: 1,
});

/** Fuera del flujo en desktop: no ensancha búsqueda + Filtros. */
export const resultsButtonPositionStyle = ({ isMobile = false } = {}) => (
  isMobile
    ? { flexShrink: 0 }
    : {
        position: 'absolute',
        left: '100%',
        top: 0,
        marginLeft: '10px',
      }
);

export const searchFieldWrapperStyle = ({ isMobile = false } = {}) => ({
  position: 'relative',
  flex: 1,
  flexShrink: 1,
  width: isMobile ? undefined : 'auto',
  maxWidth: isMobile ? undefined : `${SEARCH_BAR_WIDTH}px`,
  minWidth: 0,
});

/**
 * Contenedor del campo de búsqueda (borde + icono + input).
 * Figma: 48px alto, radius 8px, padding 11/19, gap 10, borde 2px en foco.
 */
export const searchFieldContainerStyle = ({
  focused = false,
  disabled = false,
  hasValue = false,
  parsing = false,
} = {}) => {
  const isActive = focused || hasValue || parsing;

  return {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    maxWidth: '100%',
    height: `${SEARCH_BAR_HEIGHT}px`,
    boxSizing: 'border-box',
    backgroundColor: colors.backgroundSurface,
    borderStyle: 'solid',
    borderWidth: isActive ? '2px' : '1px',
    borderColor: disabled
      ? 'rgba(255,255,255,0.08)'
      : isActive
        ? FILTER_ACCENT
        : colors.border,
    borderRadius: '8px',
    padding: isActive ? '10px 18px' : '11px 19px',
    opacity: parsing ? 0.85 : 1,
    transition: 'border-color 0.15s ease, border-width 0.15s ease, padding 0.15s ease, opacity 0.15s ease',
  };
};

export const searchIconStyle = ({
  active = false,
  disabled = false,
  nlp = false,
  thinking = false,
} = {}) => ({
  fontSize: '20px',
  width: '20px',
  height: '20px',
  flexShrink: 0,
  color: disabled
    ? 'rgba(255,255,255,0.25)'
    : nlp
      ? (active ? FILTER_ACCENT : FILTER_ACCENT_67)
      : active
        ? FILTER_ACCENT
        : colors.textSecondary,
  ...(thinking ? { animation: 'nlSearchSparklePulse 1.2s ease-in-out infinite' } : {}),
});

export const searchInputInnerStyle = ({ disabled = false } = {}) => ({
  flex: 1,
  minWidth: 0,
  width: '100%',
  border: 'none',
  outline: 'none',
  background: 'transparent',
  padding: 0,
  margin: 0,
  fontSize: typography.fontSize.sm,
  lineHeight: typography.lineHeight.normal,
  fontFamily: typography.fontFamily.primary,
  fontWeight: typography.fontWeight.regular,
  color: disabled ? 'rgba(255,255,255,0.35)' : colors.textPanelDescription,
  cursor: disabled ? 'not-allowed' : 'text',
});

export const searchClearButtonStyle = {
  flexShrink: 0,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: colors.textSecondary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2px',
  borderRadius: '4px',
  marginLeft: '4px',
};

export const searchDropdownStyle = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  left: 0,
  right: 0,
  zIndex: 1200,
  backgroundColor: colors.backgroundSurface,
  borderRadius: '8px',
  border: `1px solid ${colors.border}`,
  boxShadow: '0 8px 32px rgba(0,0,0,0.55)',
  overflow: 'hidden',
  animation: 'headerSearchDropdownIn 0.15s ease-out',
};

/** Color del botón Filtros en reposo (cerrado). */
const FILTER_BUTTON_IDLE_COLOR = '#E8F4F4';

/**
 * Botón secundario "Filtros" (Figma: 44px, radius 8, padding 10/14, gap 10).
 * Repos: #E8F4F4 · Abierto: primary.
 */
export const filterButtonStyle = ({ active = false, hovered = false } = {}) => ({
  position: 'relative',
  flexShrink: 0,
  height: `${FILTER_BUTTON_HEIGHT}px`,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  padding: '10px 14px',
  borderRadius: '8px',
  border: `1px solid ${active ? FILTER_ACCENT : FILTER_BUTTON_IDLE_COLOR}`,
  backgroundColor: active
    ? FILTER_ACCENT_14
    : hovered
      ? 'rgba(232, 244, 244, 0.08)'
      : 'transparent',
  color: active ? FILTER_ACCENT : FILTER_BUTTON_IDLE_COLOR,
  fontSize: typography.fontSize.sm,
  fontWeight: typography.fontWeight.medium,
  fontFamily: typography.fontFamily.primary,
  lineHeight: typography.lineHeight.normal,
  cursor: 'pointer',
  transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
});

export const filterIconStyle = ({ active = false } = {}) => ({
  fontSize: '20px',
  width: '20px',
  height: '20px',
  display: 'block',
  flexShrink: 0,
  color: active ? FILTER_ACCENT : FILTER_BUTTON_IDLE_COLOR,
});

export const filterBadgeStyle = {
  position: 'absolute',
  bottom: '-5px',
  right: '-5px',
  minWidth: '16px',
  height: '16px',
  borderRadius: '999px',
  backgroundColor: FILTER_ACCENT,
  color: FILTER_ACCENT_CONTRAST,
  fontSize: '9px',
  fontWeight: typography.fontWeight.bold,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
  padding: '0 4px',
  boxSizing: 'border-box',
};

export const mobileSearchBarContainerStyle = {
  position: 'fixed',
  top: `${HEADER_HEIGHT}px`,
  left: 0,
  right: 0,
  zIndex: 1095,
  padding: `${MOBILE_SEARCH_BAR_PADDING_Y}px 12px`,
  backgroundColor: colors.backgroundSurface,
  borderBottom: `1px solid ${colors.border}`,
  boxSizing: 'border-box',
};

/**
 * El FilterPanel mobile vive dentro de esta barra (stacking context 1095).
 * Con el panel de lista/detalle abierto (también 1095, más tarde en el DOM)
 * hay que subir el contexto para que Filtros no quede atrás.
 */
export const mobileSearchBarContainerWithFiltersStyle = {
  ...mobileSearchBarContainerStyle,
  zIndex: 1250,
};

export const headerCenterSlotStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minWidth: 0,
  width: '100%',
  maxWidth: '100%',
  padding: 0,
  overflow: 'visible',
  boxSizing: 'border-box',
};

export const searchPlaceholderCss = `
  .map-header-search-input::placeholder {
    color: ${colors.textSecondary};
    opacity: 1;
  }
`;

export const dropdownKeyframes = `
  @keyframes headerSearchDropdownIn {
    from { opacity: 0; transform: translateY(-4px) scale(0.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes nlSearchSparklePulse {
    0%, 100% { opacity: 0.45; transform: scale(0.92); }
    50% { opacity: 1; transform: scale(1.08); }
  }
  @keyframes nlSearchDotPulse {
    0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
    40% { opacity: 1; transform: translateY(-2px); }
  }
  .nl-search-thinking-sparkle {
    animation: nlSearchSparklePulse 1.2s ease-in-out infinite;
  }
  .nl-search-thinking-dots {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    margin-left: 2px;
  }
  .nl-search-thinking-dots > span {
    animation: nlSearchDotPulse 1.1s ease-in-out infinite;
  }
  .nl-search-thinking-dots > span:nth-child(2) { animation-delay: 0.15s; }
  .nl-search-thinking-dots > span:nth-child(3) { animation-delay: 0.3s; }
  @keyframes nlSearchChipIn {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .nl-search-result-chip {
    animation: nlSearchChipIn 0.22s ease-out both;
  }
  button.nl-search-result-chip {
    appearance: none;
    -webkit-appearance: none;
    background-color: transparent;
    color: inherit;
  }
  button.nl-search-result-chip:hover {
    background-color: var(--filter-accent-14);
  }
  @media (prefers-reduced-motion: reduce) {
    .nl-search-thinking-sparkle,
    .nl-search-thinking-dots > span,
    .nl-search-result-chip {
      animation: none !important;
    }
  }
`;
